import React from 'react';
import styled from 'styled-components';
import { CategoryData, FlavorOption, EMOJI_FLAVORS } from '../types';
import { Catalog, CatalogFlavor, PRODUCT_LINES, ProductLineId, getProductLine, isLineComingSoon } from '../lib/catalog';
import { isEmojiFlavor, MAX_FLAVORS, switchLine, toFlavorOption } from '../lib/menu';

const CategoryContainer = styled.div`
  margin: 12px 0;
  padding: 12px;
  border-radius: 8px;
  min-width: 0;
  overflow: hidden;

  :last-child {
    margin-bottom: 0;
  }

  h4 {
    font-family: 'Helvetica Now', sans-serif;
    font-weight: bold;
    margin: 15px 0 10px;
  }
`;

const CategoryHeader = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  margin-bottom: 12px;
  gap: 8px;

  h3 {
    margin: 0;
    flex: 1;
  }
`;

const FieldRow = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px 12px;
  margin: 8px 0;

  label {
    min-width: 110px;
    font-family: 'Helvetica Now', 'Helvetica Neue', Arial, sans-serif;
  }

  input[type="color"] {
    padding: 0;
    width: 50px;
    height: 30px;
  }
`;

const LinePicker = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin: 8px 0 4px;
`;

const LineButton = styled.button<{ $selected: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-height: 36px;
  padding: 6px 14px;
  border: 2px solid ${props => (props.$selected ? '#000' : '#ccc')};
  border-radius: 100px;
  background: ${props => (props.$selected ? '#caf0ff' : '#fff')};
  font-family: 'Helvetica Now', 'Helvetica Neue', Arial, sans-serif;
  font-size: 14px;
  font-weight: ${props => (props.$selected ? 700 : 400)};
  cursor: pointer;

  &:hover,
  &:focus-visible {
    border-color: #55B6E7;
  }

  img {
    height: 18px;
    width: auto;
    display: block;
  }
`;

// "Coming Soon" on a product or flavor that is still a draft in Shopify.
const SoonBadge = styled.span`
  display: inline-block;
  padding: 2px 7px;
  border-radius: 100px;
  background: #fff992;
  border: 1px solid #000;
  color: #000;
  font-family: 'Helvetica Now', 'Helvetica Neue', Arial, sans-serif;
  font-size: 11px;
  font-weight: 700;
  line-height: 1.3;
  letter-spacing: 0.02em;
  white-space: nowrap;
`;

const CardBadge = styled(SoonBadge)`
  position: absolute;
  top: 4px;
  left: 50%;
  transform: translateX(-50%);
  font-size: 10px;
`;

const SoonNotice = styled.div`
  margin: 10px 0 4px;
  padding: 10px 12px;
  border-radius: 8px;
  background: #fffde0;
  border: 1px solid #e8df7a;
  font-family: 'Helvetica Now', 'Helvetica Neue', Arial, sans-serif;
  font-size: 14px;
  line-height: 1.4;

  p {
    margin: 0 0 6px;
  }

  img {
    height: 15px;
    width: auto;
    vertical-align: -2px;
  }

  label {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    cursor: pointer;
  }
`;

const RemoveButton = styled.button`
  background: none;
  border: none;
  color: #333;
  text-decoration: underline;
  cursor: pointer;
  font-size: 14px;
  padding: 4px;
`;

const Input = styled.input`
  padding: 6px;
  border: 1px solid #ccc;
  border-radius: 4px;
  margin-right: 8px;
  width: 200px;
`;

const WonderfulFlavorsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(100px, 1fr));
  gap: 8px;
  margin: 8px 0;
  width: 100%;
`;

const WonderfulFlavorCard = styled.button<{ $selected?: boolean }>`
  position: relative;
  min-height: 104px;
  padding: 6px;
  border: 2px solid ${props => props.$selected ? '#000' : '#ccc'};
  background: ${props => props.$selected ? '#caf0ff' : '#fff'};
  font: inherit;
  color: inherit;
  border-radius: 6px;
  text-align: center;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
  min-width: 0;

  &:hover:not(:disabled),
  &:focus-visible {
    border-color: #55B6E7;
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

const WonderfulFlavorImage = styled.img`
  width: 100%;
  height: 60px;
  object-fit: contain;
  margin-bottom: 5px;
`;

const FlavorGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(100px, 1fr));
  gap: 8px;
  margin: 8px 0;
  width: 100%;
`;

const FlavorCard = styled.div`
  position: relative;
  padding: 6px;
  border: 2px solid #ccc;
  background: #fff;
  font: inherit;
  color: inherit;
  border-radius: 6px;
  text-align: center;
  cursor: pointer;
  font-size: 0.9em;
  min-width: 0;
  
  &:hover {
    border-color: #55B6E7;
  }
`;

const FlavorImage = styled.img`
  width: 40px;
  height: 40px;
  object-fit: contain;
  margin-bottom: 5px;
`;

const EmojiContainer = styled.div`
  font-size: 1.8em;
  margin-bottom: 5px;
`;

// Full names on two lines at most: 'Bubblegum' and 'Plain Pumice' used to
// be cut to 'Bubbl...' and 'Plain P...'.
const FlavorName = styled.div`
  font-size: 0.8em;
  line-height: 1.15;
  overflow-wrap: break-word;
  width: 100%;
  text-align: center;
  padding: 0 2px;
`;

const FlavorSelector = styled.div`
  display: flex;
  gap: 10px;
  margin-bottom: 10px;
`;

const Select = styled.select`
  padding: 8px;
  border: 1px solid #ccc;
  border-radius: 4px;
  flex: 1;
`;

const Button = styled.button`
  cursor: pointer;
  position: relative;
  text-transform: uppercase;
  margin-left: 0;
  margin-right: 0;
  padding: 10px 1rem;
  font-size: 14px;
  background-color: #00cbff;
  color: #000;
  border: 3px solid black;
  border-radius: 100px;
  text-decoration: none;
  overflow: visible;
  font-family: 'LEMON MILK Medium', 'LEMON MILK', sans-serif;
  width: max-content;
  font-weight: 400;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  display: flex;
  justify-content: center;
  align-items: center;

  &:hover:not(:disabled),
  &:focus-visible {
    background: #caf0ff;
    transform: scale(1.1);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

interface CategoryEditorProps {
  category: CategoryData;
  onChange: (category: CategoryData) => void;
  index: number;
  /** Live (or cached) Shopify catalog, by product line. */
  catalog: Catalog;
  /** Shown when the menu has more than one section. */
  onRemove?: () => void;
}

const nameKey = (name: string) => name.trim().toLowerCase();

export const FLOW_LOGO = '/images/flow-logo.png';
export { MAX_FLAVORS, switchLine };

const LineLabel: React.FC<{ line: ProductLineId; name: string }> = ({ line, name }) =>
  line === 'flow' ? <img src={FLOW_LOGO} alt="Flow" /> : <>{name}</>;

const CategoryEditor: React.FC<CategoryEditorProps> = ({ category, onChange, index, catalog, onRemove }) => {
  const [selectedExtra, setSelectedExtra] = React.useState<string>('');

  const availableFlavors = catalog[category.line] || [];
  const additionalFlavors = EMOJI_FLAVORS;
  const lines = PRODUCT_LINES.filter(line => line.id === category.line || (catalog[line.id] || []).length > 0);
  const fieldId = (name: string) => `section-${index}-${name}`;
  const lineComingSoon = isLineComingSoon(catalog, category.line);
  const hasComingSoon = availableFlavors.some(flavor => flavor.comingSoon);

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange({
      ...category,
      name: e.target.value
    });
  };

  const selectedIds = new Set(category.flavors.map(f => f.id));
  const selectedNames = new Set(category.flavors.map(f => nameKey(f.name)));
  const isFull = category.flavors.length >= MAX_FLAVORS;

  const handleFlavorSelect = (flavor: FlavorOption) => {
    setSelectedExtra('');
    // A flavor appears once per section; picking it again does nothing.
    if (selectedIds.has(flavor.id)) return;
    // Same name as a flavor already on the menu (the emoji Chocolate and
    // Wonderful's Chocolate): a Wonderful flavor takes the emoji's place,
    // anything else is a duplicate and is ignored.
    const sameName = category.flavors.findIndex(f => nameKey(f.name) === nameKey(flavor.name));
    if (sameName >= 0) {
      if (isEmojiFlavor(category.flavors[sameName]) && !isEmojiFlavor(flavor)) {
        onChange({
          ...category,
          flavors: category.flavors.map((f, i) => (i === sameName ? flavor : f))
        });
      }
      return;
    }
    if (isFull) return;
    onChange({
      ...category,
      flavors: [...category.flavors, flavor]
    });
  };

  const handleToggleWonderful = (flavor: CatalogFlavor) => {
    const index = category.flavors.findIndex(f => f.id === flavor.id);
    if (index >= 0) {
      handleRemoveFlavor(index);
    } else {
      handleFlavorSelect(toFlavorOption(flavor));
    }
  };

  const handleAddFlavor = () => {
    if (!selectedExtra) return;
    const flavor = additionalFlavors.find(f => f.id === selectedExtra);
    if (flavor) {
      handleFlavorSelect(flavor);
    }
  };

  const handleRemoveFlavor = (index: number) => {
    const newFlavors = [...category.flavors];
    newFlavors.splice(index, 1);
    onChange({
      ...category,
      flavors: newFlavors
    });
  };

  return (
    <CategoryContainer>
      <CategoryHeader>
        <h3>Section {index + 1}</h3>
        {onRemove && (
          <RemoveButton type="button" onClick={onRemove}>
            Remove section
          </RemoveButton>
        )}
      </CategoryHeader>

      <div role="radiogroup" aria-label={`Section ${index + 1} product`}>
        <h4>Product:</h4>
        <LinePicker>
          {lines.map(line => (
            <LineButton
              key={line.id}
              type="button"
              role="radio"
              aria-checked={line.id === category.line}
              $selected={line.id === category.line}
              onClick={() => onChange(switchLine(category, line.id, catalog))}
            >
              <LineLabel line={line.id} name={line.name} />
              {isLineComingSoon(catalog, line.id) && <SoonBadge>Coming Soon</SoonBadge>}
            </LineButton>
          ))}
        </LinePicker>
      </div>

      <FieldRow>
        <label htmlFor={fieldId('banner')}>Banner text:</label>
        <Input
          id={fieldId('banner')}
          type="text"
          value={category.name}
          onChange={handleNameChange}
          placeholder={getProductLine(category.line).menuLabel}
        />
      </FieldRow>
      <FieldRow>
        <label htmlFor={fieldId('color')}>Banner color:</label>
        <input
          id={fieldId('color')}
          type="color"
          value={category.color}
          onChange={(e) => onChange({ ...category, color: e.target.value })}
        />
      </FieldRow>

      {hasComingSoon && (
        <SoonNotice role="note">
          <p>
            {category.line === 'flow' ? <img src={FLOW_LOGO} alt="Flow" /> : getProductLine(category.line).name}{' '}
            {lineComingSoon ? 'is coming soon.' : 'has flavors coming soon.'} Flavors marked Coming Soon print with a
            Coming Soon tag until they are in the store.
          </p>
          {category.flavors.some(flavor => flavor.comingSoon) && (
            <label htmlFor={fieldId('coming-soon')}>
              <input
                id={fieldId('coming-soon')}
                type="checkbox"
                checked={!category.hideComingSoon}
                onChange={e => onChange({ ...category, hideComingSoon: !e.target.checked })}
              />
              Print the Coming Soon tag
            </label>
          )}
        </SoonNotice>
      )}

      <h4>Wonderful Dental Flavors:</h4>
      <WonderfulFlavorsGrid>
        {availableFlavors.map((flavor) => (
          <WonderfulFlavorCard
            key={flavor.id}
            type="button"
            aria-pressed={selectedIds.has(flavor.id)}
            aria-label={flavor.comingSoon ? `${flavor.name}, coming soon` : flavor.name}
            $selected={selectedIds.has(flavor.id)}
            disabled={isFull && !selectedIds.has(flavor.id) && !selectedNames.has(nameKey(flavor.name))}
            onClick={() => handleToggleWonderful(flavor)}
          >
            {flavor.comingSoon && <CardBadge>Coming Soon</CardBadge>}
            {flavor.image && (
              <WonderfulFlavorImage
                src={flavor.image}
                alt=""
                crossOrigin="anonymous"
              />
            )}
            <FlavorName>{flavor.name}</FlavorName>
          </WonderfulFlavorCard>
        ))}
      </WonderfulFlavorsGrid>

      <h4>Additional Flavors:</h4>
      <FlavorSelector>
        <Select
          aria-label={`Additional flavor for section ${index + 1}`}
          value={selectedExtra}
          onChange={(e) => setSelectedExtra(e.target.value)}
        >
          <option value="">Select additional flavor</option>
          {additionalFlavors.map((flavor) => (
            <option
              key={flavor.id}
              value={flavor.id}
              disabled={selectedIds.has(flavor.id) || selectedNames.has(nameKey(flavor.name))}
            >
              {flavor.emoji} {flavor.name}
            </option>
          ))}
        </Select>
        <Button type="button" onClick={handleAddFlavor} disabled={!selectedExtra || isFull}>Add Flavor</Button>
      </FlavorSelector>

      <h4>Selected Flavors ({category.flavors.length}/{MAX_FLAVORS}):</h4>
      <FlavorGrid>
        {category.flavors.map((flavor, idx) => (
          <FlavorCard
            as="button"
            type="button"
            key={flavor.id}
            aria-label={`Remove ${flavor.name}`}
            onClick={() => handleRemoveFlavor(idx)}
          >
            {flavor.comingSoon && <CardBadge>Coming Soon</CardBadge>}
            {flavor.image ? (
              <FlavorImage
                src={flavor.image}
                alt=""
                crossOrigin="anonymous"
              />
            ) : (
              <EmojiContainer>{flavor.emoji}</EmojiContainer>
            )}
            <FlavorName>{flavor.name}</FlavorName>
          </FlavorCard>
        ))}
        {Array(Math.max(0, MAX_FLAVORS - category.flavors.length)).fill(null).map((_, idx) => (
          <FlavorCard key={`empty-${idx}`} aria-hidden="true" style={{ cursor: 'default' }}>
            <div>Empty Slot</div>
          </FlavorCard>
        ))}
      </FlavorGrid>
    </CategoryContainer>
  );
};

export default CategoryEditor;
